import { ActionIcon, Affix, Badge, Box, Button, Group, Modal, Paper, Stack, Text, ThemeIcon, Title } from "@mantine/core";
import { IconArrowLeft, IconArrowRight, IconRobotFace } from "@tabler/icons-react";
import { useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { APP_USAGE_GUIDE_STEPS } from "@/shared/constants/appUsageGuide.const";

const COLLAB_WORKSPACE_QUESTIONNAIRE_PATH = /^\/collaborations\/[^/]+$/;

export default function AppUsageGuideBot() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [opened, setOpened] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);

  const hideFloatingLauncher =
    COLLAB_WORKSPACE_QUESTIONNAIRE_PATH.test(location.pathname) && searchParams.get("tab") === "questionnaire";

  const step = APP_USAGE_GUIDE_STEPS[stepIndex];
  const isFirst = stepIndex === 0;
  const isLast = stepIndex === APP_USAGE_GUIDE_STEPS.length - 1;

  const progressLabel = useMemo(
    () => `Step ${stepIndex + 1} of ${APP_USAGE_GUIDE_STEPS.length}`,
    [stepIndex],
  );

  if (!step) return null;

  return (
    <>
      {!hideFloatingLauncher && (
        <Affix position={{ bottom: 20, right: 36 }} zIndex={500}>
          <Box
            component="span"
            data-sidebar-tour="usage-guide-bot"
            style={{ display: "inline-block", lineHeight: 0 }}
          >
            <ActionIcon
              radius="xl"
              size={58}
              variant="filled"
              color="primary.6"
              aria-label="Open app usage guide"
              onClick={() => {
                setStepIndex(0);
                setOpened(true);
              }}
              style={{
                boxShadow: "0 10px 26px rgba(15, 23, 42, 0.30)",
              }}
            >
              <IconRobotFace size={30} />
            </ActionIcon>
          </Box>
        </Affix>
      )}

      <Modal
        opened={opened}
        onClose={() => setOpened(false)}
        centered
        size={700}
        title={
          <Group gap="xs">
            <ThemeIcon color="primary.6" radius="xl" variant="light">
              <IconRobotFace size={18} />
            </ThemeIcon>
            <Text fw={700}>How to use GTsetu</Text>
          </Group>
        }
      >
        <Paper p="md" radius="md" style={{ border: "1px solid #E5E7EB" }}>
          <Stack gap="sm">
            <Group justify="space-between" align="center">
              <Badge color="primary.6" variant="light">
                {progressLabel}
              </Badge>
              <Text size="xs" c="dimmed">
                Static guide
              </Text>
            </Group>

            <Title order={4}>{step.title}</Title>

            <Box>
              <Text fw={700} size="sm" mb={4}>
                What this page is
              </Text>
              <Text c="dimmed" size="sm">
                {step.what}
              </Text>
            </Box>

            <Box>
              <Text fw={700} size="sm" mb={4}>
                Why this page matters
              </Text>
              <Text c="dimmed" size="sm">
                {step.why}
              </Text>
            </Box>

            <Box>
              <Text fw={700} size="sm" mb={6}>
                How to use it
              </Text>
              {step.howToUse.map((item) => (
                <Text key={item} size="sm" mb={6}>
                  - {item}
                </Text>
              ))}
            </Box>

            <Group justify="space-between" mt="xs">
              <Button
                variant="default"
                leftSection={<IconArrowLeft size={16} />}
                onClick={() => setStepIndex((prev) => Math.max(0, prev - 1))}
                disabled={isFirst}
              >
                Previous
              </Button>

              <Group>
                {step.path && (
                  <Button
                    variant="light"
                    onClick={() => {
                      setOpened(false);
                      navigate(step.path as string);
                    }}
                  >
                    Open this section
                  </Button>
                )}
                <Button
                  rightSection={!isLast ? <IconArrowRight size={16} /> : undefined}
                  onClick={() => {
                    if (isLast) {
                      setOpened(false);
                      return;
                    }
                    setStepIndex((prev) => Math.min(APP_USAGE_GUIDE_STEPS.length - 1, prev + 1));
                  }}
                >
                  {isLast ? "Finish" : "Next"}
                </Button>
              </Group>
            </Group>
          </Stack>
        </Paper>
      </Modal>
    </>
  );
}
